<template>
  <div class="h-full">
    <BaseTable
      v-if="documentTypes"
      :headers="tableHeaders"
      :list="filteredDocumentTypes"
      :total-records="documentTypesTotalCount"
      :page="documentTypesPageNumber"
      :items-per-page="documentTypesItemsPerPage"
      table-id="document-types"
      clickable
      hide-grouping
      :is-add-new-hidden="mode === 'view'"
      :disable-multiselect="mode === 'view'"
      @delete-multiselect="onDeleteMultiselect"
      @add-new="onAddNew"
      @refresh-data="onRefreshData"
      @table-item-clicked="onEdit"
      @apply-filters="onApplyFilters"
      is-saving-layout-hidden>
      <template #custom="{ item, header }">
        <span v-if="header === 'exportMethodType'" class="pl-4 text-sm text-gray-700">{{
          getExportMethodTypeName(item.exportMethodType)
        }}</span>
        <span v-if="header === 'lastExportedDate'" class="pl-4 text-sm text-gray-700">{{
          isoDateToFullDate(item.lastExportedDate) || '-'
        }}</span>
      </template>

      <template #actions="{ item }" v-if="mode == 'edit'">
        <div class="flex items-center justify-center gap-1">
          <button
            v-if="item.isFromManager"
            class="p-1.5 text-red-400 rounded-full hover:bg-red-50"
            type="button"
            @click="onDeleteRow(item)">
            <IconTrashOutline class="w-4 h-4" />
          </button>
        </div>
      </template>
      <template #filters>
        <div class="grid grid-cols-3 mx-10 my-5">
          <!-- Col 1 -->
          <div class="space-y-4 justify-self-start">
            <div>
              <BaseTriStateToggle
                :label="$t('representative.isMapped')"
                name="isInactiveFilter"
                v-model="filterIsMapped" />
            </div>
          </div>
        </div>
      </template>
    </BaseTable>
    <DCDocumentTypeModalCreateEdit
      v-if="createEditModalActive && currentDocumentType"
      :document-type="currentDocumentType"
      :mode="createEditModalMode"
      :view-only="createEditModalMode === 'view'"
      :representative-id="props.representativeId || ''"
      :existing-document-types="documentTypes"
      @close-modal="createEditModalActive = false"
      @save="onSave" />

    <BaseModalConfirm
      v-if="modalDeleteRow"
      @cancel="modalDeleteRow = false"
      @confirm="onDeleteSingle()" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { v4 as uuidv4 } from 'uuid';
import type { DCDocumentType, RepresentativeFiltersData } from '@/types/DataCollection';
import { eIOMethodType } from '@/types/DataCollection';
import BaseTable from '../common/BaseTable.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import BaseModalConfirm from '../common/BaseModalConfirm.vue';
import DCDocumentTypeModalCreateEdit from './DCDocumentTypeModalCreateEdit.vue';
import BaseTriStateToggle from '../common/BaseTriStateToggle.vue';
import { deepCopy } from '@/utils/data';
import { RecordMode, type TableFilterData } from '@/types/Base';
import { getDCDocumentTypesWithConditions } from '@/services/api/dataCollection';
import { isoDateToFullDate } from '@/utils/date';

const { t: $t } = useI18n();

const props = defineProps<{
  mode: 'view' | 'edit';
  isHorizontalLayout?: boolean;
  resetState: boolean;
  representativeId?: string;
}>();

const documentTypes = defineModel<DCDocumentType[]>('documentTypes', { default: [] });
const isDocumentTypeModified = defineModel<boolean>('isDocumentTypeModified', { default: false });
const filterIsMapped = ref<boolean | undefined>(undefined);

// Modal state
const createEditModalActive = ref(false);
const currentDocumentType = ref<DCDocumentType>();
const createEditModalMode = ref<'add' | 'edit' | 'view'>('add');

const originalDocumentTypes = ref<DCDocumentType[]>([]);

const documentTypesTotalCount = ref<number>(0);
const documentTypesItemsPerPage = ref(50);
const documentTypesPageNumber = ref(1);

const tableHeaders = [
  {
    value: 'documentType.name',
    text: $t('baseObject.name'),
    type: 'text',
    active: true,
  },
  {
    value: 'documentType.code',
    text: $t('baseObject.code'),
    type: 'text',
    active: true,
  },
  {
    value: 'documentTypeRemotedCode',
    text: $t('representative.remotedCode'),
    type: 'text',
    active: true,
  },
  {
    value: 'toApprove',
    text: $t('representative.toApprove'),
    type: 'check',
    active: true,
  },
  {
    value: 'exportMethodType',
    text: $t('representative.exportMethodType'),
    type: 'custom',
    active: true,
  },
  {
    value: 'lastExportedDate',
    text: $t('representative.lastExportedDate'),
    type: 'custom',
    active: true,
  },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

const filteredDocumentTypes = computed<DCDocumentType[]>(() => {
  if (!documentTypes.value) return [];
  let list = documentTypes.value.filter((x) => x.recordMode !== RecordMode.Deleted);

  return list;
});

watch(
  () => documentTypes.value,
  (newData) => {
    isDocumentTypeModified.value =
      JSON.stringify(newData) !== JSON.stringify(originalDocumentTypes.value);
  },
  { deep: true }
);

watch(
  () => props.resetState,
  async (newVal, oldVal) => {
    if (props.representativeId && (oldVal === undefined || (newVal && oldVal !== newVal))) {
      await loadDocumentTypes();
    }
  },
  { immediate: true }
);

async function loadDocumentTypes(
  params?: TableFilterData & {
    isMapped?: boolean;
    representativeId?: string;
  }
) {
  const queryParams = {
    page: params?.page || documentTypesPageNumber.value,
    itemsPerPage: params?.itemsPerPage || documentTypesItemsPerPage.value,
    query: params?.query || '',
    sortColumn: params?.column !== undefined ? tableHeaders[params.column]?.value : undefined,
    sortAscending: params?.order === 'asc' || params?.order === undefined,
    isMapped: params?.isMapped,
    representativeId: props.representativeId,
  };

  const res = await getDCDocumentTypesWithConditions(queryParams);
  if (res) {
    originalDocumentTypes.value = res.data;
    documentTypes.value = deepCopy<DCDocumentType[]>(originalDocumentTypes.value);
    documentTypesTotalCount.value = res.count;

    if (params?.page !== undefined) {
      documentTypesPageNumber.value = params.page;
    }
    if (params?.itemsPerPage !== undefined) {
      documentTypesItemsPerPage.value = params.itemsPerPage;
    }
  }
}

const modalDeleteRow = ref(false);
const deleteRowItem = ref<DCDocumentType>();

function onDeleteRow(item: DCDocumentType) {
  deleteRowItem.value = deepCopy(item);
  modalDeleteRow.value = true;
}

function onDeleteSingle() {
  if (deleteRowItem.value && documentTypes.value) {
    const index = documentTypes.value.findIndex((x) => x.id === deleteRowItem.value!.id);
    if (index >= 0) {
      if (documentTypes.value[index].recordMode === RecordMode.Added) {
        documentTypes.value.splice(index, 1);
      } else {
        documentTypes.value[index].recordMode = RecordMode.Deleted;
      }
    }
  }
  modalDeleteRow.value = false;
  deleteRowItem.value = undefined;
}

function onDeleteMultiselect(items: DCDocumentType[]) {
  if (!documentTypes.value) return;

  const indicesToRemove: number[] = [];
  const indicesToMarkDeleted: number[] = [];

  items.forEach((item) => {
    const index = documentTypes.value!.findIndex((x) => x.id === item.id);
    if (index >= 0) {
      if (documentTypes.value![index].recordMode === RecordMode.Added) {
        indicesToRemove.push(index);
      } else {
        indicesToMarkDeleted.push(index);
      }
    }
  });

  indicesToMarkDeleted.forEach((index) => {
    documentTypes.value![index].recordMode = RecordMode.Deleted;
  });

  indicesToRemove
    .sort((a, b) => b - a)
    .forEach((index) => {
      documentTypes.value!.splice(index, 1);
    });
}

async function onRefreshData(options: TableFilterData) {
  await loadDocumentTypes(options);
}

function initDocumentType(): DCDocumentType {
  return {
    id: uuidv4(),
    documentTypeId: '',
    representativeId: props.representativeId || '',
    documentTypeRemotedCode: '',
    toApprove: false,
    exportMethodType: eIOMethodType.Csv,
    isActive: true,
    isFromManager: true,
    documentType: {
      id: '',
      name: '',
      code: '',
    },
    recordMode: RecordMode.Added,
  };
}

function onAddNew() {
  currentDocumentType.value = initDocumentType();
  createEditModalMode.value = 'add';
  createEditModalActive.value = true;
}

function onEdit(documentType: DCDocumentType) {
  currentDocumentType.value = deepCopy(documentType);
  createEditModalMode.value = props.mode === 'view' ? 'view' : 'edit';
  createEditModalActive.value = true;
}

function onSave(documentType: DCDocumentType) {
  createEditModalActive.value = false;

  if (createEditModalMode.value === 'add') {
    documentTypes.value.push(documentType);
  } else {
    const index = documentTypes.value.findIndex((x) => x.id === documentType.id);
    if (index >= 0) {
      if (documentTypes.value[index].recordMode !== RecordMode.Added) {
        documentType.recordMode = RecordMode.Modified;
      }
      documentTypes.value[index] = documentType;
    }
  }
}

function getExportMethodTypeName(methodType: number): string {
  switch (methodType) {
    case eIOMethodType.Csv:
      return 'CSV';
    case eIOMethodType.FixedLength:
      return 'Fixed Length';
    case eIOMethodType.json:
      return 'JSON';
    case eIOMethodType.RestApi:
      return 'REST API';
    default:
      return 'Unknown';
  }
}

async function onApplyFilters(data: RepresentativeFiltersData) {
  data.isMapped = filterIsMapped.value;
  await onRefreshData(data);
}
</script>
